import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Users, IndianRupee } from 'lucide-react';
import { api, fmtMoney } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

export default function CustomersListPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const nav = useNavigate();

    const load = useCallback(async (query = '') => {
        try {
            setLoading(true);
            const r = await api.invListCustomers({ q: query || undefined });
            setItems(r.items || []);
        } catch (e) {
            toast.error('Failed to load customers');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4" data-testid="customers-list-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-heading font-semibold tracking-tight">Customers</h1>
                    <p className="text-sm text-muted-foreground mt-1">Manage the businesses you invoice.</p>
                </div>
                <Button asChild data-testid="btn-new-customer">
                    <Link to="/invoicing/customers/new"><Plus size={16} className="mr-1.5" /> New customer</Link>
                </Button>
            </div>

            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1 max-w-md">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                data-testid="customers-search"
                                placeholder="Search customers..."
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') load(q); }}
                                className="pl-9"
                            />
                        </div>
                        <Button variant="outline" size="sm" onClick={() => load(q)} data-testid="customers-search-btn">Search</Button>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? <div className="p-6"><Skeleton className="h-40 w-full" /></div> : items.length === 0 ? (
                        <div className="text-center py-12">
                            <Users size={40} className="mx-auto mb-3 opacity-30" />
                            <div className="text-sm text-muted-foreground mb-3">No customers yet.</div>
                            <Button asChild data-testid="empty-add-customer"><Link to="/invoicing/customers/new">Add your first customer</Link></Button>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>GSTIN</TableHead>
                                    <TableHead>Phone</TableHead>
                                    <TableHead>City / State</TableHead>
                                    <TableHead className="text-right">Payment Terms</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((c) => (
                                    <TableRow
                                        key={c.id}
                                        data-testid={`customer-row-${c.id}`}
                                        className="cursor-pointer"
                                        onClick={() => nav(`/invoicing/customers/${c.id}`)}
                                    >
                                        <TableCell className="font-medium">{c.name}</TableCell>
                                        <TableCell className="font-mono text-xs">{c.gstin || '—'}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground">{c.phone || '—'}</TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {[c.billing_address?.city, c.billing_address?.state].filter(Boolean).join(', ') || '—'}
                                        </TableCell>
                                        <TableCell className="text-right text-xs">Net {c.payment_terms_days || 30}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
